import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { CONSTANTS } from '../data';
import { CardType, PlayerPosition, CardStack, PlayersType, ScoresType, StealableStack, GameMode } from '../types';
import { AudioManager } from '../lib/AudioManager';

const shuffleDeck = (array: CardType[]): CardType[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export default function useGameEngine(audioManager: AudioManager) {
  // --- GAME STATE ---
  const [gameMode, setGameMode] = useState<GameMode>(null);
  const [showTitle, setShowTitle] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showPlayerSelection, setShowPlayerSelection] = useState(false);
  const [deck, setDeck] = useState<CardType[]>([]);
  const [players, setPlayers] = useState<PlayersType>({ top: [], right: [], bottom: [], left: [] });
  const [scores, setScores] = useState<ScoresType>({ top: 0, right: 0, bottom: 0, left: 0 });
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [drawnCard, setDrawnCard] = useState<CardType | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isScoring, setIsScoring] = useState(false);
  const [cardBeingPlaced, setCardBeingPlaced] = useState<string | null>(null);
  const [bustingPlayer, setBustingPlayer] = useState<PlayerPosition | null>(null);
  const [bustingCards, setBustingCards] = useState<CardType[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [stealableStacks, setStealableStacks] = useState<StealableStack[]>([]);
  const [showStealMessage, setShowStealMessage] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [finalWinners, setFinalWinners] = useState<string[]>([]);
  const [finalWinnerScore, setFinalWinnerScore] = useState<number>(0);
  const [speedLevel, setSpeedLevel] = useState<1 | 2 | 4>(1);
  const [isPaused, setIsPaused] = useState(false);
  const [activePlayerPositions, setActivePlayerPositions] = useState<PlayerPosition[]>(CONSTANTS.PLAYER_POSITIONS);
  const [playerNames, setPlayerNames] = useState<Record<PlayerPosition, string>>(CONSTANTS.CPU_PLAYER_NAMES);

  const isInitialMount = useRef(true);

  const initialDeck = useMemo(() => {
    let idCounter = 0;
    const createdDeck: CardType[] = [];
    CONSTANTS.CARD_DISTRIBUTION.forEach(({ number, color, count, imageAlias }) => {
      for (let i = 0; i < count; i++) {
        createdDeck.push({
          id: `card-${idCounter++}`,
          color,
          number,
          imageAlias,
        });
      }
    });
    return shuffleDeck(createdDeck);
  }, []);

  // Helper to reset all state for restart or new game
  const resetGame = useCallback(() => {
    setDeck(shuffleDeck(initialDeck));
    setPlayers({ top: [], right: [], bottom: [], left: [] });
    setScores({ top: 0, right: 0, bottom: 0, left: 0 });
    setCurrentPlayerIndex(0);
    setDrawnCard(null);
    setIsDrawing(false);
    setIsScoring(false);
    setCardBeingPlaced(null);
    setBustingPlayer(null);
    setBustingCards([]);
    setShowConfetti(false);
    setStealableStacks([]);
    setShowStealMessage(false);
    setIsGameOver(false);
    setFinalWinners([]);
    setFinalWinnerScore(0);
    setSpeedLevel(1);
    setIsPaused(false);
  }, [initialDeck]);

  // --- FIX: Ensure music continues during instructions and player selection modal ---
  useEffect(() => {
    if (showTitle || showInstructions || showPlayerSelection || !isGameOver) {
      audioManager.startBackgroundMusic();
    } else {
      audioManager.stopBackgroundMusic();
    }
  }, [showTitle, showInstructions, showPlayerSelection, isGameOver, audioManager]);
  // --- END FIX ---

  // When the app first loads, set up the deck
  useEffect(() => {
    setDeck(initialDeck);
  }, [initialDeck]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const activePlayerPosition = activePlayerPositions[currentPlayerIndex] as PlayerPosition;
    const playerStacks = players[activePlayerPosition];

    if (playerStacks.length > 0) {
      setIsScoring(true);
      setShowConfetti(true);
      audioManager.playSound('turnEnd');
      const cardsToScore = playerStacks.flat();
      const points = cardsToScore.reduce((sum, card) => sum + card.number, 0);

      setScores(prevScores => ({
        ...prevScores,
        [activePlayerPosition]: prevScores[activePlayerPosition] + points,
      }));

      setPlayers(prevPlayers => ({
        ...prevPlayers,
        [activePlayerPosition]: [],
      }));

      setTimeout(() => {
        setIsScoring(false);
      }, 500);

      setTimeout(() => {
        setShowConfetti(false);
      }, CONSTANTS.CONFETTI_DURATION * 1000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayerIndex, activePlayerPositions]);

  const activePlayerPosition = activePlayerPositions[currentPlayerIndex] as PlayerPosition;
  const isHumanTurn = gameMode === 'hotseat' || (gameMode === 'vs_cpu' && activePlayerPosition === 'bottom');

  // Reset speed when it's the human's turn
  useEffect(() => {
    if (isHumanTurn) {
      setSpeedLevel(1);
    }
  }, [isHumanTurn]);

  // --- TIMING CONFIG ---
  const speedConfig = CONSTANTS.SPEED_LEVELS[speedLevel];
  const drawTime = speedConfig.DRAW_DURATION;
  const bustAnimationTime = speedConfig.BUST_ANIMATION_DURATION;
  const bustCleanupDelay = speedConfig.BUST_CLEANUP_DELAY;
  const stealMessageTime = speedConfig.STEAL_MESSAGE_DURATION;
  const aiThinkTime = speedConfig.AI_THINKING_TIME_MS;
  const animationDelay = speedConfig.ANIMATION_DELAY;

  // --- BUST LOGIC ---
  const checkForBust = useCallback((
    playerPosition: PlayerPosition,
    newStacks: CardStack[],
    drawnCardValue: number
  ) => {
    const matchingStacks = newStacks.filter(stack => stack.length > 0 && stack[0].number === drawnCardValue);
    const hasBustingStack = matchingStacks.some(stack => stack.length >= CONSTANTS.BUST_THRESHOLD);

    if (hasBustingStack) {
      const allCards = newStacks.flat();
      setBustingCards(allCards);
      setBustingPlayer(playerPosition);
      audioManager.playSound('bust');

      setTimeout(() => {
        setPlayers(prevPlayers => ({
          ...prevPlayers,
          [playerPosition]: []
        }));

        setTimeout(() => {
          setBustingPlayer(null);
          setBustingCards([]);
          setTimeout(() => {
            setCurrentPlayerIndex((prevIndex) => (prevIndex + 1) % activePlayerPositions.length);
            setIsDrawing(false);
          }, animationDelay);
        }, bustAnimationTime * 1000 + bustCleanupDelay);

      }, 500);

      return true;
    }

    return false;
  }, [audioManager, bustAnimationTime, bustCleanupDelay, activePlayerPositions.length, animationDelay]);

  const handleEndTurn = useCallback(() => {
    if (isDrawing || bustingPlayer || isScoring || isGameOver || isPaused) return;
    audioManager.playSound('buttonClick');
    setStealableStacks([]);
    setTimeout(() => {
      setCurrentPlayerIndex((prevIndex) => (prevIndex + 1) % activePlayerPositions.length);
    }, animationDelay);
  }, [isDrawing, bustingPlayer, isScoring, isGameOver, isPaused, audioManager, activePlayerPositions.length, animationDelay]);

  // --- STEAL LOGIC ---
  const checkStealableStacks = useCallback((drawnCard: CardType, players: PlayersType, currentPlayer: PlayerPosition): StealableStack[] => {
    const stealable: StealableStack[] = [];
    for (const pos of activePlayerPositions as PlayerPosition[]) {
      if (pos === currentPlayer) continue;
      players[pos].forEach((stack, idx) => {
        if (stack.length > 0 && stack[0].number === drawnCard.number) {
          stealable.push({
            player: pos,
            stackIndex: idx,
            cards: [...stack],
          });
        }
      });
    }
    return stealable;
  }, [activePlayerPositions]);

  const handleDrawCard = useCallback(() => {
    if (isDrawing || deck.length === 0 || drawnCard || bustingPlayer || isScoring || isGameOver || isPaused) return;

    setIsDrawing(true);
    audioManager.playSound('cardDraw');
    const cardToDraw = deck[0];
    setDrawnCard(cardToDraw);

    setTimeout(() => {
      const currentPlayerPosition = activePlayerPositions[currentPlayerIndex] as PlayerPosition;
      audioManager.playSound('cardPlace');

      // Create the new stacks first, without setting state yet
      const currentStacks = players[currentPlayerPosition];
      const stackIndex = currentStacks.findIndex(stack => stack.length > 0 && stack[0].number === cardToDraw.number);
      let newStacks;
      if (stackIndex > -1) {
        newStacks = currentStacks.map((stack, index) => {
          if (index === stackIndex) {
            return [...stack, cardToDraw];
          }
          return stack;
        });
      } else {
        newStacks = [...currentStacks, [cardToDraw]];
      }

      // Now check for bust with the potential new stacks
      const isBusting = checkForBust(currentPlayerPosition, newStacks, cardToDraw.number);

      // Now update all states
      setPlayers(prevPlayers => ({
        ...prevPlayers,
        [currentPlayerPosition]: newStacks,
      }));
      setDeck(prevDeck => prevDeck.slice(1));
      setDrawnCard(null);
      setCardBeingPlaced(cardToDraw.id);

      if (!isBusting) {
        setStealableStacks(checkStealableStacks(cardToDraw, players, currentPlayerPosition));
        
        // New logic to replace handleCardPlacementComplete
        const placementCompletionDelay = speedLevel > 1 ? (animationDelay * 2) : 500;

        setTimeout(() => {
          setCardBeingPlaced(null);
          setIsDrawing(false);
        }, placementCompletionDelay);
      }
      // If isBusting, the bust logic will handle setting isDrawing to false.

    }, drawTime);
  }, [
    isDrawing, deck, drawnCard, bustingPlayer, isScoring, isGameOver, isPaused, 
    audioManager, currentPlayerIndex, checkForBust, drawTime, activePlayerPositions, 
    checkStealableStacks, players, speedLevel, animationDelay
  ]);

  const handleSteal = useCallback(() => {
    if (stealableStacks.length === 0 || isPaused) return;
    audioManager.playSound('steal');
    const currentPlayerPosition = activePlayerPositions[currentPlayerIndex] as PlayerPosition;

    setPlayers(prevPlayers => {
      const updatedPlayers: PlayersType = { ...prevPlayers };

      const stealsByPlayer = stealableStacks.reduce((acc, s) => {
        if (!acc[s.player]) {
          acc[s.player] = [];
        }
        acc[s.player].push(s.stackIndex);
        return acc;
      }, {} as Record<PlayerPosition, number[]>);

      Object.keys(stealsByPlayer).forEach(playerStr => {
        const player = playerStr as PlayerPosition;
        const indicesToRemove = new Set(stealsByPlayer[player]);
        updatedPlayers[player] = prevPlayers[player].filter((_, index) => !indicesToRemove.has(index));
      });

      const stolenStacks = stealableStacks.map(s => s.cards);
      const allStacks = [...prevPlayers[currentPlayerPosition], ...stolenStacks];
      const stacksByNumber: { [num: number]: CardType[] } = {};
      allStacks.forEach(stack => {
        if (stack.length === 0) return;
        const number = stack[0].number;
        if (!stacksByNumber[number]) {
          stacksByNumber[number] = [];
        }
        stacksByNumber[number].push(...stack);
      });
      const mergedStacks: CardStack[] = Object.values(stacksByNumber);

      updatedPlayers[currentPlayerPosition] = mergedStacks;

      return updatedPlayers;
    });

    setShowStealMessage(true);
    setTimeout(() => {
      setShowStealMessage(false);
    }, stealMessageTime);

    setTimeout(() => {
      setStealableStacks([]);
      setIsDrawing(false);
    }, animationDelay);
  }, [stealableStacks, isPaused, audioManager, currentPlayerIndex, stealMessageTime, activePlayerPositions, animationDelay]);

  // --- GAME OVER LOGIC ---
  useEffect(() => {
    if (
      deck.length === 0 &&
      !drawnCard &&
      !isGameOver &&
      !bustingPlayer &&
      !isScoring
    ) {
      setIsGameOver(true);
      audioManager.playSound('gameOver');

      setTimeout(() => {
        setPlayers(prevPlayers => {
          const newScores: ScoresType = { ...scores };
          for (const pos of activePlayerPositions as PlayerPosition[]) {
            const stacks = prevPlayers[pos];
            const cards = stacks.flat();
            const points = cards.reduce((sum, card) => sum + card.number, 0);
            newScores[pos] += points;
          }
          setScores(newScores);

          const activeScores = activePlayerPositions.map(pos => newScores[pos]);
          const maxScore = activeScores.length > 0 ? Math.max(...activeScores) : 0;

          const winnerPositions = activePlayerPositions
            .filter(pos => newScores[pos] === maxScore)
            .map(pos => playerNames[pos as PlayerPosition]);
            
          setFinalWinners(winnerPositions);
          setFinalWinnerScore(maxScore);

          return {
            top: [],
            right: [],
            bottom: [],
            left: [],
          };
        });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck.length, drawnCard, isGameOver, bustingPlayer, isScoring, scores, activePlayerPositions, playerNames, audioManager]);

  // --- AI TURN LOGIC ---
  useEffect(() => {
    const isCpuTurn =
      gameMode === 'vs_cpu' &&
      !isGameOver &&
      !isDrawing &&
      !bustingPlayer &&
      !isScoring &&
      !isPaused &&
      activePlayerPositions[currentPlayerIndex] !== 'bottom';

    if (isCpuTurn) {
      const activePlayerPosition = activePlayerPositions[currentPlayerIndex] as PlayerPosition;

      const aiThinkTimeout = setTimeout(() => {
        if (stealableStacks.length > 0) {
          handleSteal();
          return;
        }

        const playerStacks = players[activePlayerPosition];
        const riskyStacksCount = playerStacks.filter(
          stack => stack.length === CONSTANTS.BUST_THRESHOLD - 1
        ).length;

        const probabilities = CONSTANTS.AI_CONFIG.DRAW_AGAIN_PROBABILITY;
        const drawProbability = probabilities[Math.min(riskyStacksCount, probabilities.length - 1)];
        const mustDraw = playerStacks.length === 0;

        if (mustDraw || Math.random() < drawProbability) {
          handleDrawCard();
        } else {
          handleEndTurn();
        }
      }, aiThinkTime);

      return () => clearTimeout(aiThinkTimeout);
    }
  }, [gameMode, currentPlayerIndex, isGameOver, isDrawing, bustingPlayer, isScoring, players, stealableStacks, aiThinkTime, isPaused, handleSteal, handleDrawCard, handleEndTurn, activePlayerPositions]);

  const handleSelectMode = useCallback((mode: 'hotseat' | 'vs_cpu') => {
    setGameMode(mode);
    setShowTitle(false);
    if (mode === 'vs_cpu') {
      setActivePlayerPositions(CONSTANTS.PLAYER_POSITIONS);
      setPlayerNames(CONSTANTS.CPU_PLAYER_NAMES);
      setShowInstructions(true);
    } else {
      setShowPlayerSelection(true);
    }
  }, []);

  const handlePlayerSelectionComplete = useCallback((numPlayers: number) => {
    const setup = CONSTANTS.PLAYER_SETUP[numPlayers as keyof typeof CONSTANTS.PLAYER_SETUP];
    setActivePlayerPositions(setup.positions);
    setPlayerNames(setup.names);
    setShowPlayerSelection(false);
    setShowInstructions(true);
  }, []);

  const handlePlayerSelectionBack = useCallback(() => {
    setShowPlayerSelection(false);
    setShowTitle(true);
    setGameMode(null);
  }, []);

  const handleInstructionsComplete = useCallback(() => {
    setShowInstructions(false);
    resetGame();
  }, [resetGame]);

  const handleRestart = useCallback(() => {
    resetGame();
    setGameMode(null);
    setShowTitle(true);
    setIsPaused(false);
  }, [resetGame]);

  const handleDrawCardClick = useCallback(() => {
    audioManager.playSound('buttonClick');
    handleDrawCard();
  }, [handleDrawCard, audioManager]);

  const handleStealClick = useCallback(() => {
    audioManager.playSound('buttonClick');
    handleSteal();
  }, [handleSteal, audioManager]);

  const handlePause = useCallback((pause: boolean) => {
    audioManager.playSound('buttonClick');
    setIsPaused(pause);
  }, [audioManager]);

  const handleToggleSpeed = useCallback(() => {
    audioManager.playSound('buttonClick');
    setSpeedLevel(prev => {
      if (prev === 1) return 2;
      if (prev === 2) return 4;
      return 1;
    });
  }, [audioManager]);

  return {
    // Game state
    gameMode, showTitle, showInstructions, showPlayerSelection, deck, players, scores, currentPlayerIndex,
    drawnCard, isDrawing, isScoring, cardBeingPlaced, bustingPlayer, bustingCards, showConfetti,
    stealableStacks, showStealMessage, isGameOver, finalWinners, finalWinnerScore, speedLevel,
    isPaused, activePlayerPositions, playerNames, activePlayerPosition, isHumanTurn, bustAnimationTime,
    // Handlers
    handleSelectMode, handlePlayerSelectionComplete, handlePlayerSelectionBack, handleInstructionsComplete,
    handleRestart, handleDrawCardClick, handleStealClick, handlePause, handleToggleSpeed,
    handleEndTurn,
  };
}
